import type { FormField, ValueFormField } from '@sitecore-jss/sitecore-jss-forms';

/** ARIA attributes that associate a form field with its validation errors. */
export interface FormFieldAriaProps {
  'aria-describedby'?: string;
  'aria-invalid'?: true;
}

/**
 * Builds an id for an element associated with a form field (such as an error message or helper text).
 * @param field - A form field.
 * @param idSuffix - A suffix to include after the field's base id.
 * @returns An id for the associated element, or undefined.
 */
const getAssociatedElementId = (field: FormField, idSuffix: string): string | undefined => {
  const { valueField } = field as FormField & Partial<ValueFormField>;

  return valueField?.id ? `${valueField.id}-${idSuffix}` : undefined;
};

/**
 * Builds an error message id.
 * @param field - A form field.
 * @returns An error message id or undefined.
 */
export const getFormFieldErrorId = (field: FormField): string | undefined => {
  return getAssociatedElementId(field, 'error');
};

/**
 * Builds a helper text id.
 * @param field - A form field.
 * @returns A helper text id or undefined.
 */
export const getFormFieldHelperTextId = (field: FormField): string | undefined => {
  return getAssociatedElementId(field, 'helper-text');
};

/**
 * Builds an ARIA attributes object.
 * @param field - A form field.
 * @param errors - An array of error messages.
 * @param ariaDescribedById - An `aria-describedby` id.
 * @returns An ARIA attributes object.
 */
export const getFormFieldAriaProps = (
  field: FormField,
  errors: string[] | undefined,
  ariaDescribedById?: string
): FormFieldAriaProps => {
  const hasErrors = errors?.length;

  if (!hasErrors) {
    return {};
  }

  const errorId = getFormFieldErrorId(field);
  const ariaDescribedBy = [ariaDescribedById, errorId].filter((id) => id !== undefined).join(' ');

  return {
    'aria-describedby': ariaDescribedBy || undefined,
    'aria-invalid': true,
  };
};
